import React, { FC, useMemo } from 'react'
import { LinkAsProvider } from '@moonpig/launchpad-components'
import {
  shouldForwardProp,
  StyleSheetManager,
  ThemeProvider,
} from '@moonpig/launchpad-utils'
import { theme } from '@moonpig/launchpad-theme'
import { AppShellProps, AppShell } from '@moonpig/web-core-app-shell'
import { FlagsProvider } from '@moonpig/web-core-flags'
import {
  LoggedInProvider,
  LoginModalProvider,
  AuthProvider,
  GoogleOneTapProvider,
} from '@moonpig/web-core-auth'
import { FetchProvider, Fetch } from '@moonpig/web-core-fetch'
import {
  PageRequiresLoginProvider,
  RemindersModalProvider,
  NotificationsProvider,
  PlatformProvider,
} from '@moonpig/web-core-context'
import {
  BrowserErrorTracker,
  BrowserErrorTrackerProvider,
} from '@moonpig/web-core-monitoring'
import { LocaleTextProvider as LaunchpadLocaleTextProvider } from '@moonpig/launchpad-localisation'
import { DefaultContentSecurityPolicy } from '@moonpig/web-core-security'
import { useLocale, StoreProvider } from '@moonpig/web-core-stores'
import { createEppoClient, useExperiment } from '@moonpig/web-core-eppo/browser'
import { EppoContext, SessionIdProvider } from '@moonpig/web-core-utils'
import { EppoConfiguration } from '@moonpig/web-core-eppo/types'
import { Region } from '@moonpig/web-core-types'
import { SetupGTM } from '@moonpig/web-core-analytics'
import { Link } from '@moonpig/web-core-link'
import { DefaultSubjectAttributes } from '@moonpig/web-core-eppo/shared/DefaultSubjectAttributes'
import { EppoExperimentOverrides } from '@moonpig/web-core-utils/src/eppoContext/EppoContext'
import { CustomerSegment } from '@moonpig/web-core-types-graphql'
import { CreateSailthruCookie } from './CreateSailthruCookie'
import { LoginModal } from './LoginModal'
import { SendGAUserData } from './SendGAUserData'
import { useLoginGuard } from '../../hooks'
import { SendGALoginEvent } from './SendGALoginEvent'
import { CreateIntercomSession } from './CreateIntercomSession/CreateIntercomSession'
import { MixpanelSetup } from './MixPanel/MixpanelSetup'
import { CacheCustomerSegments } from './CacheCustomerSegments/CacheCustomerSegments'

const DynamicallySetupGTM: FC<{
  pageTracking: { type: string; disableDefaultPageTracking?: boolean }
  enableThirdPartyScripts?: boolean
}> = ({ pageTracking, enableThirdPartyScripts }) => {
  const validationEnabled =
    useExperiment('analytics-validation', {
      fallback: 'control',
    }) === 'cookie-enabled'

  const stapeEnabled = useExperiment('web-enablement-stape-gtm', {
    fallback: false,
  })

  return (
    <SetupGTM
      enableThirdPartyScripts={enableThirdPartyScripts}
      pageTracking={pageTracking}
      stapeEnabled={stapeEnabled}
      validationEnabled={validationEnabled}
    />
  )
}

type Flags = { [id: string]: boolean }

export const AppContainer: FC<
  React.PropsWithChildren<{
    appShellProps: AppShellProps
    authBaseUrl?: string
    browserErrorTracker: BrowserErrorTracker
    enableThirdPartyScripts?: boolean
    fetch: Fetch
    flags: Flags
    isMobile: boolean
    notifications: React.ReactNode[]
    pageRequiresLogin: boolean
    pageTracking: { type: string; disableDefaultPageTracking?: boolean }
    region: Region
    sessionId: string
    loggedIn: boolean
    eppoConfiguration: EppoConfiguration
    eppoDefaultSubjectAttributes: DefaultSubjectAttributes
    eppoExperimentOverrides: EppoExperimentOverrides
    customerSegments: Array<CustomerSegment> | null
  }>
> = ({
  appShellProps,
  authBaseUrl,
  browserErrorTracker,
  children,
  enableThirdPartyScripts,
  fetch,
  flags,
  isMobile,
  notifications,
  pageRequiresLogin,
  pageTracking,
  region,
  sessionId,
  loggedIn,
  eppoConfiguration,
  eppoDefaultSubjectAttributes,
  eppoExperimentOverrides,
  customerSegments,
}) => {
  const eppoClient = createEppoClient({
    flagsConfiguration: eppoConfiguration,
    isBot: eppoDefaultSubjectAttributes.isBot,
  })

  const eppoContext = useMemo(
    () =>
      eppoClient
        ? {
            client: eppoClient,
            defaultSubjectAttributes: eppoDefaultSubjectAttributes,
            experimentOverrides: eppoExperimentOverrides,
          }
        : null,
    [eppoClient, eppoDefaultSubjectAttributes, eppoExperimentOverrides],
  )

  const { isCustomerLoggedIn } = useLoginGuard(loggedIn, pageRequiresLogin)
  const { language } = useLocale()
  const storeId = region

  return (
    <EppoContext.Provider value={eppoContext}>
      <StoreProvider storeId={storeId}>
        <FetchProvider fetch={fetch}>
          <FlagsProvider flags={flags}>
            <PageRequiresLoginProvider value={pageRequiresLogin}>
              <LoggedInProvider value={{ loggedIn: isCustomerLoggedIn }}>
                <RemindersModalProvider value={{ show: false }}>
                  <NotificationsProvider value={{ notifications }}>
                    <SessionIdProvider sessionId={sessionId}>
                      <MixpanelSetup url={appShellProps.path} />
                      <CacheCustomerSegments segments={customerSegments} />
                      <BrowserErrorTrackerProvider
                        browserErrorTracker={browserErrorTracker}
                      >
                        <LoginModalProvider>
                          <AuthProvider authBaseUrl={authBaseUrl}>
                            <GoogleOneTapProvider>
                              <PlatformProvider isMobile={isMobile}>
                                <>
                                  <StyleSheetManager
                                    shouldForwardProp={shouldForwardProp}
                                  >
                                    <ThemeProvider theme={theme}>
                                      <LaunchpadLocaleTextProvider
                                        locale={language}
                                      >
                                        <LinkAsProvider
                                          linkAs={
                                            Link as React.ComponentProps<
                                              typeof LinkAsProvider
                                            >['linkAs']
                                          }
                                        >
                                          <AppShell {...appShellProps}>
                                            <DynamicallySetupGTM
                                              enableThirdPartyScripts={
                                                enableThirdPartyScripts
                                              }
                                              pageTracking={pageTracking}
                                            />
                                            <SendGALoginEvent />
                                            <SendGAUserData />
                                            <CreateSailthruCookie />
                                            <CreateIntercomSession />
                                            <DefaultContentSecurityPolicy />
                                            <LoginModal />
                                            {children}
                                          </AppShell>
                                        </LinkAsProvider>
                                      </LaunchpadLocaleTextProvider>
                                    </ThemeProvider>
                                  </StyleSheetManager>
                                </>
                              </PlatformProvider>
                            </GoogleOneTapProvider>
                          </AuthProvider>
                        </LoginModalProvider>
                      </BrowserErrorTrackerProvider>
                    </SessionIdProvider>
                  </NotificationsProvider>
                </RemindersModalProvider>
              </LoggedInProvider>
            </PageRequiresLoginProvider>
          </FlagsProvider>
        </FetchProvider>
      </StoreProvider>
    </EppoContext.Provider>
  )
}
